import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAdmin } from "./useAdmin";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { useSeo, PRIVATE_SEO } from "@/lib/seo";

export default function AdminLoginPage() {
  useSeo({ title: "LUX staff sign in", ...PRIVATE_SEO });
  const navigate = useNavigate();
  const { refetch, invalidate } = useAdmin();
  const [step, setStep] = useState("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submitPassword = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/admin/auth/login", { email, password });
      setStep("totp");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const submitTotp = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (code.length !== 6) {
      setError("Enter the 6-digit code from your authenticator");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/auth/totp", { code });
      toast.success("Signed in");
      invalidate();
      await refetch();
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(errMsg(err));
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="adm-card p-6 sm:p-8" data-testid="admin-login-page">
      <p className="adm-label">Staff access</p>
      <h1 className="mt-2 text-2xl font-semibold text-admin-text">Sign in to the staff console</h1>
      <p className="mt-1 text-sm text-admin-muted">All staff sign-ins require password + authenticator code. Actions are recorded in the audit log.</p>

      {step === "password" && (
        <form onSubmit={submitPassword} className="mt-6 space-y-4" noValidate>
          <div>
            <label htmlFor="admin-email" className="adm-label">Email</label>
            <input id="admin-email" type="email" required autoComplete="email" className="adm-input mt-1.5"
              value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email-input" />
          </div>
          <div>
            <label htmlFor="admin-pw" className="adm-label">Password</label>
            <div className="relative mt-1.5">
              <input id="admin-pw" type={showPw ? "text" : "password"} required autoComplete="current-password"
                className="adm-input pr-10" value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-login-password-input" />
              <button type="button" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1.5 text-admin-muted hover:text-admin-text"
                data-testid="admin-login-password-toggle">
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          {error && <p role="alert" className="text-sm text-[#B42318]" data-testid="admin-login-error">{error}</p>}
          <button type="submit" disabled={busy} className="adm-btn adm-btn-primary w-full" data-testid="admin-login-submit">
            {busy ? "Signing in..." : "Continue"}
          </button>
        </form>
      )}

      {step === "totp" && (
        <form onSubmit={submitTotp} className="mt-6 space-y-4">
          <p className="adm-label">Second step</p>
          <p className="text-sm text-admin-muted">Enter the 6-digit code from your authenticator app.</p>
          <InputOTP maxLength={6} value={code} onChange={setCode} containerClassName="gap-2 justify-center" data-testid="admin-totp-input" autoFocus>
            <InputOTPGroup>
              {Array.from({ length: 6 }).map((_, i) => (
                <InputOTPSlot key={i} index={i} className="h-12 w-11 border-admin-border text-lg" />
              ))}
            </InputOTPGroup>
          </InputOTP>
          {error && <p role="alert" className="text-sm text-[#B42318]" data-testid="admin-login-error">{error}</p>}
          <button type="submit" disabled={busy || code.length !== 6} className="adm-btn adm-btn-primary w-full" data-testid="admin-totp-submit">
            {busy ? "Verifying..." : "Sign in"}
          </button>
          <p className="text-center text-xs text-admin-muted">
            Not working? <button type="button" onClick={() => { setStep("password"); setError(""); setCode(""); }} className="underline" data-testid="admin-totp-back">Start over</button>
          </p>
        </form>
      )}
    </section>
  );
}
